/* eslint-disable react-refresh/only-export-components -- provider + hook in one module */
import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, getAccessToken, onUnauthorized, refreshAccessToken, setAccessToken } from '@/lib/api';
import type { AuthUser, LoginResponse } from '@/auth/types';

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

export interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  login: (email: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  /** Re-fetch `/auth/me` (e.g. after changing the name or password). */
  reload: () => Promise<void>;
  /** Role helpers. VIEWER is read-only; use `canWrite` to hide mutating actions. */
  isAdmin: boolean;
  canWrite: boolean;
}

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

/**
 * Holds the signed-in user. On mount it restores the session: if an access token is in
 * sessionStorage it calls `/auth/me`, otherwise it tries the refresh cookie once. While that
 * runs `status === 'loading'` and `RequireAuth` shows a splash instead of redirecting.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = React.useState<AuthStatus>('loading');
  const [user, setUser] = React.useState<AuthUser | null>(null);

  const signOutLocally = React.useCallback(() => {
    setAccessToken(null);
    setUser(null);
    setStatus('anonymous');
    queryClient.clear();
  }, [queryClient]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let token = getAccessToken();
        if (!token) token = await refreshAccessToken();
        if (!token) {
          if (!cancelled) signOutLocally();
          return;
        }
        const me = await api.get<AuthUser>('/auth/me');
        if (!cancelled) {
          setUser(me);
          setStatus('authenticated');
        }
      } catch {
        if (!cancelled) signOutLocally();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [signOutLocally]);

  // The api client tells us when a refresh failed mid-session.
  React.useEffect(() => onUnauthorized(signOutLocally), [signOutLocally]);

  const login = React.useCallback(async (email: string, password: string) => {
    const res = await api.post<LoginResponse>('/auth/login', { email, password }, { noRefresh: true });
    setAccessToken(res.accessToken);
    setUser(res.user);
    setStatus('authenticated');
    return res.user;
  }, []);

  const logout = React.useCallback(async () => {
    try {
      await api.post('/auth/logout', undefined, { noRefresh: true });
    } catch {
      /* the cookie may already be gone; sign out locally anyway */
    }
    signOutLocally();
  }, [signOutLocally]);

  const reload = React.useCallback(async () => {
    const me = await api.get<AuthUser>('/auth/me');
    setUser(me);
    setStatus('authenticated');
  }, []);

  const value = React.useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      login,
      logout,
      reload,
      isAdmin: user?.role === 'ADMIN',
      canWrite: user?.role === 'ADMIN' || user?.role === 'RECRUITER',
    }),
    [status, user, login, logout, reload],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
